import React, { useState } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Collapse,
  Empty,
  Tooltip,
  message,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '@/services/api.service';

const { Option } = Select;
const { Panel } = Collapse;

const FORM_TYPES = [
  { key: 'BILANS_STANJA', label: 'Bilans stanja', color: 'blue' },
  { key: 'BILANS_USPEHA', label: 'Bilans uspeha', color: 'purple' },
];

const COMPANY_SIZE_COLORS = {
  MIKRO: 'cyan',
  MALO: 'orange',
  SREDNJE: 'gold',
  VELIKO: 'magenta',
};

export default function MappingsPanel({
  mappings,
  _loading,
  companySizes,
  getLocalizedLabel,
}) {
  const { t } = useTranslation(['tables', 'forms', 'messages', 'menus']);
  const fs = (key) => t(`financialStatements.${key}`, { ns: 'tables' });
  const queryClient = useQueryClient();

  const [mappingModalVisible, setMappingModalVisible] = useState(false);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [mappingForm] = Form.useForm();
  const [ruleForm] = Form.useForm();

  // Create mapping mutation
  const createMappingMutation = useMutation({
    mutationFn: (data) => API.financialStatements.createAopMapping(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-mappings']);
      setMappingModalVisible(false);
      mappingForm.resetFields();
      message.success(t('createSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Update mapping mutation
  const updateMappingMutation = useMutation({
    mutationFn: ({ mappingId, data }) => API.financialStatements.updateAopMapping(mappingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-mappings']);
      setMappingModalVisible(false);
      setSelectedMapping(null);
      mappingForm.resetFields();
      message.success(t('updateSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Delete mapping mutation
  const deleteMappingMutation = useMutation({
    mutationFn: (mappingId) => API.financialStatements.deleteAopMapping(mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-mappings']);
      message.success(t('deleteSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Add rule mutation
  const addRuleMutation = useMutation({
    mutationFn: ({ mappingId, rule }) => API.financialStatements.addMappingRule(mappingId, rule),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-mappings']);
      setRuleModalVisible(false);
      ruleForm.resetFields();
      message.success(t('createSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Update rule mutation
  const updateRuleMutation = useMutation({
    mutationFn: ({ mappingId, ruleId, data }) =>
      API.financialStatements.updateMappingRule(mappingId, ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-mappings']);
      setRuleModalVisible(false);
      setSelectedRule(null);
      ruleForm.resetFields();
      message.success(t('updateSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Delete rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: ({ mappingId, ruleId }) =>
      API.financialStatements.deleteMappingRule(mappingId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-mappings']);
      message.success(t('deleteSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  const handleOpenMappingModal = (mapping = null) => {
    setSelectedMapping(mapping);
    if (mapping) {
      mappingForm.setFieldsValue({
        companySize: mapping.companySize,
        formType: mapping.formType,
        name: mapping.name,
        isActive: mapping.isActive,
      });
    } else {
      mappingForm.resetFields();
    }
    setMappingModalVisible(true);
  };

  const handleSaveMapping = async () => {
    try {
      const values = await mappingForm.validateFields();

      if (selectedMapping) {
        updateMappingMutation.mutate({ mappingId: selectedMapping._id, data: values });
      } else {
        createMappingMutation.mutate(values);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleOpenRuleModal = (mapping, rule = null) => {
    setSelectedMapping(mapping);
    setSelectedRule(rule);

    if (rule) {
      // Map database fields to form fields
      ruleForm.setFieldsValue({
        accountPrefix: rule.accountPrefix || '',
        aopCode: rule.aopCode || '',
        aopName: rule.aopName || rule.description || '',
        useDebit: rule.useDebit ?? false,
        useCredit: rule.useCredit ?? false,
        useAbsoluteValue: rule.useAbsoluteValue ?? false,
        signMultiplier: rule.signMultiplier ?? 1,
        priority: rule.priority ?? 0,
        notes: rule.notes || '',
      });
    } else {
      ruleForm.resetFields();
      ruleForm.setFieldsValue({
        signMultiplier: 1,
        priority: 0,
        useDebit: false,
        useCredit: false,
        useAbsoluteValue: false,
      });
    }
    setRuleModalVisible(true);
  };

  const handleSaveRule = async () => {
    try {
      const values = await ruleForm.validateFields();

      if (selectedRule) {
        updateRuleMutation.mutate({
          mappingId: selectedMapping._id,
          ruleId: selectedRule._id,
          data: values,
        });
      } else {
        addRuleMutation.mutate({
          mappingId: selectedMapping._id,
          rule: values,
        });
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const ruleColumns = [
    {
      title: fs('accountPrefix'),
      dataIndex: 'accountPrefix',
      key: 'accountPrefix',
      width: 120,
    },
    {
      title: fs('aopCode'),
      dataIndex: 'aopCode',
      key: 'aopCode',
      width: 100,
    },
    {
      title: fs('aopName'),
      dataIndex: 'aopName',
      key: 'aopName',
      width: 300,
      ellipsis: true,
      render: (val, record) => val || record.description || '-',
    },
    {
      title: fs('useDebit'),
      dataIndex: 'useDebit',
      key: 'useDebit',
      width: 100,
      align: 'center',
      render: (val) => val ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '-',
    },
    {
      title: fs('useCredit'),
      dataIndex: 'useCredit',
      key: 'useCredit',
      width: 100,
      align: 'center',
      render: (val) => val ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '-',
    },
    {
      title: fs('signMultiplier'),
      dataIndex: 'signMultiplier',
      key: 'signMultiplier',
      width: 100,
      align: 'center',
      render: (val) => val === -1 ? <Tag color="red">-1</Tag> : <Tag color="green">+1</Tag>,
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 100,
      render: (_, rule) => (
        <Space>
          <Tooltip title={t('edit', { ns: 'menus' })}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenRuleModal(selectedMapping, rule)}
            />
          </Tooltip>
          <Popconfirm
            title={fs('confirmDeleteRule')}
            onConfirm={() => deleteRuleMutation.mutate({
              mappingId: selectedMapping._id,
              ruleId: rule._id,
            })}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!mappings || mappings.length === 0) {
    return (
      <div>
        <div className="flex justify-end mb-4">
          <Button
            type="primary"
            className="btn-primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenMappingModal()}
          >
            {fs('createMapping')}
          </Button>
        </div>
        <Empty description={fs('noMappings')} />

        {/* Mapping Modal */}
        <MappingModal
          visible={mappingModalVisible}
          onCancel={() => {
            setMappingModalVisible(false);
            setSelectedMapping(null);
          }}
          onSave={handleSaveMapping}
          form={mappingForm}
          selectedMapping={selectedMapping}
          companySizes={companySizes}
          getLocalizedLabel={getLocalizedLabel}
          loading={createMappingMutation.isPending || updateMappingMutation.isPending}
          fs={fs}
          t={t}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          type="primary"
          className="btn-primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenMappingModal()}
        >
          {fs('createMapping')}
        </Button>
      </div>

      <Collapse accordion className="[&_.ant-collapse-expand-icon]:!self-center">
        {mappings.map((mapping) => (
          <Panel
            key={mapping._id}
            header={
              <div className="flex items-center justify-between w-full pr-4">
                <Space>
                  <span className="font-medium">{mapping.name}</span>
                  <Tag color={COMPANY_SIZE_COLORS[mapping.companySize] || 'default'}>
                    {getLocalizedLabel(companySizes.find((s) => s.key === mapping.companySize) || {})}
                  </Tag>
                  <Tag color={FORM_TYPES.find((f) => f.key === mapping.formType)?.color || 'default'}>
                    {FORM_TYPES.find((f) => f.key === mapping.formType)?.label}
                  </Tag>
                  <Tag color={mapping.isActive ? 'green' : 'default'}>
                    {mapping.isActive ? fs('active') : fs('inactive')}
                  </Tag>
                </Space>
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="default"
                    className="btn-default btn-default--hover"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleOpenMappingModal(mapping)}
                  />
                  <Popconfirm
                    title={fs('confirmDeleteMapping')}
                    onConfirm={() => deleteMappingMutation.mutate(mapping._id)}
                  >
                    <Button type="default" className="btn-default" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              </div>
            }
          >
            <div className="mb-2">
              <Button
                type="default"
                className="btn-default btn-default--hover"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedMapping(mapping);
                  handleOpenRuleModal(mapping);
                }}
              >
                {fs('addRule')}
              </Button>
            </div>
            <Table
              dataSource={mapping.mappings || []}
              columns={ruleColumns.map((col) => ({
                ...col,
                render: col.key === 'actions'
                  ? (_, rule) => (
                      <Space>
                        <Tooltip title={t('edit', { ns: 'menus' })}>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenRuleModal(mapping, rule)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title={fs('confirmDeleteRule')}
                          onConfirm={() => deleteRuleMutation.mutate({
                            mappingId: mapping._id,
                            ruleId: rule._id,
                          })}
                        >
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )
                  : col.render,
              }))}
              rowKey="_id"
              size="small"
              pagination={false}
              scroll={{ x: 920 }}
            />
          </Panel>
        ))}
      </Collapse>

      {/* Mapping Modal */}
      <MappingModal
        visible={mappingModalVisible}
        onCancel={() => {
          setMappingModalVisible(false);
          setSelectedMapping(null);
        }}
        onSave={handleSaveMapping}
        form={mappingForm}
        selectedMapping={selectedMapping}
        companySizes={companySizes}
        getLocalizedLabel={getLocalizedLabel}
        loading={createMappingMutation.isPending || updateMappingMutation.isPending}
        fs={fs}
        t={t}
      />

      {/* Rule Modal */}
      <Modal
        title={selectedRule ? fs('editRule') : fs('addRule')}
        open={ruleModalVisible}
        onCancel={() => {
          setRuleModalVisible(false);
          setSelectedRule(null);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="default"
              className="btn-default btn-default--hover"
              onClick={() => {
                setRuleModalVisible(false);
                setSelectedRule(null);
              }}
            >
              {t('cancel', { ns: 'forms' })}
            </Button>
            <Button
              type="primary"
              className="btn-primary"
              loading={addRuleMutation.isPending || updateRuleMutation.isPending}
              onClick={handleSaveRule}
            >
              {t('save', { ns: 'forms' })}
            </Button>
          </div>
        }
      >
        <Form form={ruleForm} layout="vertical">
          <Form.Item
            name="accountPrefix"
            label={fs('accountPrefix')}
            rules={[{ required: true, message: fs('accountPrefixRequired') }]}
          >
            <Input placeholder="e.g., 02, 241, 0281" />
          </Form.Item>
          <Form.Item
            name="aopCode"
            label={fs('aopCode')}
            rules={[{ required: true, message: fs('aopCodeRequired') }]}
          >
            <Input placeholder="e.g., 0009, 1016" />
          </Form.Item>
          <Form.Item name="aopName" label={fs('aopName')}>
            <Input placeholder={fs('aopNamePlaceholder')} />
          </Form.Item>
          <Space>
            <Form.Item name="useDebit" label={fs('useDebit')} valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="useCredit" label={fs('useCredit')} valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="useAbsoluteValue" label={fs('useAbsoluteValue')} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="signMultiplier" label={fs('signMultiplier')}>
            <Select>
              <Option value={1}>+1</Option>
              <Option value={-1}>-1</Option>
            </Select>
          </Form.Item>
          <Form.Item name="priority" label={fs('priority')}>
            <InputNumber min={0} max={100} />
          </Form.Item>
          <Form.Item name="notes" label={fs('notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function MappingModal({
  visible,
  onCancel,
  onSave,
  form,
  selectedMapping,
  companySizes,
  getLocalizedLabel,
  loading,
  fs,
  t,
}) {
  return (
    <Modal
      title={selectedMapping ? fs('editMapping') : fs('createMapping')}
      open={visible}
      onCancel={onCancel}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="default"
            className="btn-default btn-default--hover"
            onClick={onCancel}
          >
            {t('cancel', { ns: 'forms' })}
          </Button>
          <Button
            type="primary"
            className="btn-primary"
            loading={loading}
            onClick={onSave}
          >
            {t('save', { ns: 'forms' })}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="companySize"
          label={fs('companySize')}
          rules={[{ required: true, message: fs('companySizeRequired') }]}
        >
          <Select placeholder={fs('selectCompanySize')} disabled={!!selectedMapping}>
            {companySizes.map((size) => (
              <Option key={size.key} value={size.key}>
                {getLocalizedLabel(size)}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="formType"
          label={fs('formType')}
          rules={[{ required: true, message: fs('formTypeRequired') }]}
        >
          <Select placeholder={fs('selectFormType')} disabled={!!selectedMapping}>
            {FORM_TYPES.map((type) => (
              <Option key={type.key} value={type.key}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="name" label={fs('mappingName')}>
          <Input placeholder={fs('mappingNamePlaceholder')} />
        </Form.Item>
        <Form.Item name="isActive" label={fs('isActive')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
